// frontend/src/App.tsx
import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { TabProvider } from './contexts/TabContext';
import { publicRoutes } from './routes/PublicRoutes';
import { privateRoutes } from './routes/PrivateRoutes';
import { ROUTES } from './constants/FrontendPathConstants';
import { SignUpPage } from './pages/Auth/SignUpPage';
import { LoginPage } from './pages/Auth/LoginPage';
import ProfilePage from './pages/SidePages/ProfilePage';
import ReportSummaryPage from './pages/SidePages/ReportSummaryPage';
import {
  Button,
  Text,
  Flex,
} from '@chakra-ui/react';

import {
  saveAccessExpiryFromToken,
  startSessionWatchdogs,
  isExpiredSoon,
  logout,
  setupTabCloseAutoLogout,
} from './pages/Auth/session';

/* ================== LIGHTWEIGHT INLINE MODAL ================== */
const InlineModal: React.FC<{ message: string; onClose?: () => void }> = ({ message, onClose }) => {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(26, 18, 98, 0.4)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(92vw, 440px)',
          borderRadius: 16,
          background: '#fff',
          padding: 32,
          boxShadow: '0 20px 40px rgba(26, 18, 98, 0.15)',
          textAlign: 'center',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 12, color: '#1a1262', fontWeight: 700, fontSize: 20 }}>Notice</h3>
        <p style={{ margin: 0, fontSize: 16, color: '#8c89b1', lineHeight: 1.5 }}>{message}</p>
        <button
          onClick={onClose}
          style={{
            marginTop: 24,
            padding: '12px 32px',
            borderRadius: 8,
            border: 'none',
            background: '#1a1262',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 14,
            transition: 'all 0.2s ease',
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = '#4f4987')}
          onMouseOut={(e) => (e.currentTarget.style.background = '#1a1262')}
        >
          OK
        </button>
      </div>
    </div>
  );
};

/* PipelineGate: small modal gate for unfinished pages */
const PipelineGate: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const p = pathname.toLowerCase();

  // Coming Soon set 
  const isComingSoon =
    p.includes('bulk-rna-seq') ||
    p.includes('scrna-seq') ||
    p.includes('chip-seq') ||
    p.includes('atac-seq') ||
    p.includes('somatic-variant') ||
    p.includes('deep-variant') ||
    p.includes('mutect-2') ||
    p.includes('bisulfite-seq');

  // Contact-only set (exclude haplotype-caller which is now available)
  const isContactOnly =
    !p.includes('haplotype-caller') && (
      p.includes('genome') ||
      p.includes('alignment') ||
      p.includes('align') ||
      p.includes('somatic') ||
      p.includes('mutect') ||
      p.includes('germline')
    );

  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (isComingSoon) setMsg('Coming Soon');
    else if (isContactOnly) setMsg('Contact us for the access');
  }, [isComingSoon, isContactOnly]);

  // Auto-close after exactly 1s and return to Introduction
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => {
      setMsg(null);
      navigate(ROUTES.DASHBOARD, { replace: true });
    }, 1000);
    return () => clearTimeout(t);
  }, [msg, navigate]);

  if (msg) {
    return (
      <InlineModal
        message={msg}
        onClose={() => navigate(ROUTES.DASHBOARD, { replace: true })}
      />
    );
  }

  if (!isComingSoon && !isContactOnly) return <Outlet />;
  return null;
};

const AuthCallback = () => {
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const message = urlParams.get('message');

    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userData');
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('access_exp_at');
    localStorage.removeItem('last_active_at');

    if (success === 'true') {
      // Handle both password reset and signup completion
      if (message === 'password_reset_success') {
        window.location.href = `${ROUTES.LOGIN}?message=password_reset_success`;
      } else {
        // This is signup completion (password set successfully)
        window.location.href = `${ROUTES.LOGIN}?message=signup_success`;
      }
    } else {
      window.location.href = ROUTES.LOGIN;
    }
  }, []);

  return (
    <div style={{ textAlign: 'center', marginTop: '40vh' }}>
      <p>Processing authentication...</p>
      <p>Please wait...</p>
    </div>
  );
};

const readAuthState = () =>
  localStorage.getItem('isAuthenticated') === 'true' &&
  !!localStorage.getItem('userData');

const App: React.FC = () => {
  const [bootstrapped, setBootstrapped] = useState(false);
  const [isAuthed, setIsAuthed] = useState<boolean>(readAuthState());

  useEffect(() => {
    const hasToken = !!localStorage.getItem('access_token');

    if (hasToken) {
      if (!localStorage.getItem('access_exp_at')) {
        saveAccessExpiryFromToken();
      }
      if (isExpiredSoon()) {
        logout('token expired on load');
        setBootstrapped(true);
        setIsAuthed(false);
        return;
      }
      startSessionWatchdogs();
      setupTabCloseAutoLogout();
    }
    setBootstrapped(true);
  }, []);

  useEffect(() => {
    const onAuthChanged = () => setIsAuthed(readAuthState());
    window.addEventListener('auth-changed', onAuthChanged);
    window.addEventListener('storage', onAuthChanged); // cross-tab

    // run once too
    onAuthChanged();
    return () => {
      window.removeEventListener('auth-changed', onAuthChanged);
      window.removeEventListener('storage', onAuthChanged);
    };
  }, []);

  if (!bootstrapped) {
    return (
      <div style={{ textAlign: 'center', marginTop: '40vh' }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <TabProvider defaultTab="jobs">
      <Routes>
        {!isAuthed && (
          <>
            <Route path="/" element={<Navigate to={ROUTES.LOGIN} replace />} />
            {publicRoutes}
          </>
        )}

        {isAuthed && (
          <>
            <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />

            {/* ===== DEV TEMP PIPELINE GATE (REMOVE WHEN PAGES READY) ===== */}
            <Route element={<PipelineGate />}>{privateRoutes}</Route>
            {/* ===== END DEV TEMP PIPELINE GATE ===== */}
          </>
        )}

        {/* Public explicit routes */}
        <Route path={ROUTES.SIGNUP} element={<SignUpPage />} />
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.PROFILE} element={<ProfilePage />} />

        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/reports/:jobId" element={<ReportSummaryPage />} />

        {/* Fallback */}
        <Route
          path="*"
          element={<Navigate to={isAuthed ? ROUTES.DASHBOARD : ROUTES.LOGIN} replace />}
        />
      </Routes>

    </TabProvider>
  );
};

export default App;
